// pages/LoginPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) =>
    theme.backgroundGradient || 'linear-gradient(135deg, #2cc2d4, #007bff)'};
  padding: 20px;

  /* Defensive: keep this page above any stray overlays */
  position: relative;
  z-index: 10000;
  isolation: isolate;
`;

const Form = styled.form`
  background-color: white;
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 1rem;
  outline-color: ${({ theme }) => theme.secondaryColor || '#2cc2d4'};
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background-color: ${({ theme }) => theme.primaryColor || '#2cc2d4'};
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${({ theme }) => theme.primaryHover || '#27a4ad'};
  }
`;

const Error = styled.p`
  color: #e74c3c;
  margin-bottom: 16px;
`;

const ForgotPasswordLink = styled(Link)`
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
  color: #3498db;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  `;

function LoginPage() {
  const [username, setUsername] = useState('');
  const usernameInputRef = useRef(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  const { login } = useAuth();
  const { refreshUserData } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    usernameInputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const { success } = await login(username, password);
    if (!success) {
      setError('Invalid credentials.');
      return;
    }

    try {
      await refreshUserData(); // We’ll use blog data in PostLoginPage
      navigate('/post-login');
    } catch (err) {
      console.error('Refresh error', err);
      setError('Failed to fetch blogs.');
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <h2>Login</h2>
        {error && <Error>{error}</Error>}

        <Input
          ref={usernameInputRef}
          type="text"
          name="username"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          spellCheck="false"
          required
        />

        <Input
          type="password"
          name="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <Button type="submit">Login</Button>

        <ForgotPasswordLink to="/request-password-reset">
          Forgot Password?
        </ForgotPasswordLink>
      </Form>
    </Container>
  );
}

export default LoginPage;
